import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, Upload } from 'lucide-react';
import type { ComplianceItem, ComplianceStatus, ComplianceType } from '@ip/shared-types';
import {
  COMPLIANCE_STATUSES, COMPLIANCE_TYPES, EMPLOYER_MANAGED_COMPLIANCE_TYPES,
} from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { cn } from '@/lib/utils';
import { downloadCsv, formatDateMedium, todayYmd } from '@/lib/hr';
import { t } from '@/i18n';
import {
  COMPLIANCE_STATUS_CHIP, complianceDaysLabel, complianceDaysTone,
  complianceStatusLabel, complianceTypeLabel,
} from './complianceMeta';
import { ComplianceReviewQueue } from './ComplianceReviewQueue';

interface ComplianceAdminTableProps {
  nip: string;
}

/**
 * "Terminy i zgodność" (HR admin tab): every employee × requirement with
 * status and days-left, sorted by soonest expiry (API order), filterable by
 * type/status/department, exportable to CSV. The pending-verification queue
 * sits on top so HR can decide when there is no line manager.
 */
export function ComplianceAdminTable({ nip }: ComplianceAdminTableProps) {
  const { listCompliance, verifyCompliance, submitCompliance } = useHrApi();
  const [items, setItems] = useState<ComplianceItem[] | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | ComplianceType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ComplianceStatus>('all');
  const [dept, setDept] = useState('all');
  const [markingId, setMarkingId] = useState<string | null>(null);

  // Employer-managed types (medical exam / BHP training): hr/admin records the
  // document here — it lands in the employee file and is valid immediately.
  const [recordFor, setRecordFor] = useState<ComplianceItem | null>(null);
  const [recordValidUntil, setRecordValidUntil] = useState('');
  const [recordBusy, setRecordBusy] = useState(false);
  const recordFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;
    listCompliance(nip)
      .then((rows) => { if (mounted) setItems(rows); })
      .catch((error) => {
        if (mounted) setItems([]);
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      });
    return () => { mounted = false; };
  }, [nip, listCompliance]);

  /** rud_report is reminder-only: HR marks the ZUS filing as done (→ valid). */
  const markRudDone = async (item: ComplianceItem) => {
    setMarkingId(item.id);
    try {
      const updated = await verifyCompliance(nip, item.id);
      setItems((prev) => (prev ?? []).map((i) => (i.id === updated.id ? updated : i)));
      toast.success(t('hr.compliance.rudMarkedDone'));
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error((error as Error).message || t('hr.common.loadFailed'));
      }
    } finally {
      setMarkingId(null);
    }
  };

  const handleRecord = async () => {
    const file = recordFileRef.current?.files?.[0];
    if (!recordFor || !file || !recordValidUntil) return;
    setRecordBusy(true);
    try {
      const updated = await submitCompliance(nip, recordFor.id, file, recordValidUntil);
      setItems((prev) => (prev ?? []).map((i) => (i.id === updated.id ? updated : i)));
      toast.success(t('hr.compliance.recordSuccess'));
      setRecordFor(null);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error(t('hr.compliance.actionFailed'), {
          description: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      setRecordBusy(false);
    }
  };

  const departments = useMemo(
    () => [...new Set((items ?? []).map((i) => i.department).filter((d): d is string => !!d))].sort(),
    [items],
  );

  const filtered = useMemo(
    () => (items ?? []).filter(
      (i) =>
        (typeFilter === 'all' || i.type === typeFilter) &&
        (statusFilter === 'all' || i.status === statusFilter) &&
        (dept === 'all' || i.department === dept),
    ),
    [items, typeFilter, statusFilter, dept],
  );

  const exportCsv = () => {
    downloadCsv(`zgodnosc-${nip}-${todayYmd()}.csv`, [
      [
        t('hr.compliance.colEmployee'), t('hr.compliance.colDepartment'), t('hr.compliance.colType'),
        t('hr.compliance.colStatus'), t('hr.compliance.colValidUntil'), t('hr.compliance.colDaysLeft'),
        t('hr.compliance.colDocument'),
      ],
      ...filtered.map((i) => [
        i.employeeName, i.department, complianceTypeLabel(i.type),
        t(`hr.compliance.status.${i.status}`), i.validUntil, i.daysLeft,
        i.documentFileName,
      ]),
    ]);
  };

  if (items === null) return <Skeleton className="h-72 w-full" />;

  return (
    <div className="flex flex-col gap-4">
      <ComplianceReviewQueue nip={nip} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as 'all' | ComplianceType)}>
            <SelectTrigger className="h-8 w-44" aria-label={t('hr.compliance.filterType')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('hr.compliance.filterAll')}</SelectItem>
              {COMPLIANCE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{complianceTypeLabel(type)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | ComplianceStatus)}>
            <SelectTrigger className="h-8 w-44" aria-label={t('hr.compliance.filterStatus')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('hr.compliance.filterAll')}</SelectItem>
              {COMPLIANCE_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>{t(`hr.compliance.status.${status}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {departments.length > 0 && (
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="h-8 w-40" aria-label={t('hr.compliance.filterDepartment')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('hr.grid.allDepartments')}</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
          <Download className="size-3.5" />
          {t('hr.compliance.exportCsv')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('hr.compliance.colEmployee')}</TableHead>
              <TableHead>{t('hr.compliance.colDepartment')}</TableHead>
              <TableHead>{t('hr.compliance.colType')}</TableHead>
              <TableHead>{t('hr.compliance.colStatus')}</TableHead>
              <TableHead>{t('hr.compliance.colValidUntil')}</TableHead>
              <TableHead>{t('hr.compliance.colDaysLeft')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.employeeName}</TableCell>
                <TableCell className="text-muted-foreground">{item.department ?? '—'}</TableCell>
                <TableCell>{complianceTypeLabel(item.type)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn('font-normal', COMPLIANCE_STATUS_CHIP[item.status])}>
                    {complianceStatusLabel(item)}
                  </Badge>
                  {item.status === 'rejected' && item.rejectedReason && (
                    <span className="text-muted-foreground ml-2 text-xs italic" title={item.rejectedReason}>
                      {t('hr.compliance.rejectedReason', { reason: item.rejectedReason })}
                    </span>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">
                  {item.validUntil ? formatDateMedium(item.validUntil) : '—'}
                </TableCell>
                <TableCell className={cn('text-xs', complianceDaysTone(item.daysLeft))}>
                  {complianceDaysLabel(item) ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  {item.type === 'rud_report' && item.status !== 'valid' && item.canVerify && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7"
                      disabled={markingId === item.id}
                      onClick={() => markRudDone(item)}
                    >
                      {t('hr.compliance.rudMarkDone')}
                    </Button>
                  )}
                  {EMPLOYER_MANAGED_COMPLIANCE_TYPES.includes(item.type) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7"
                      onClick={() => {
                        setRecordValidUntil('');
                        setRecordFor(item);
                      }}
                    >
                      <Upload className="size-3.5" />
                      {t('hr.compliance.recordDocument')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-6 text-center text-sm">
                  {t('hr.compliance.queueEmpty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!recordFor} onOpenChange={(open) => !open && setRecordFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {recordFor
                ? `${complianceTypeLabel(recordFor.type)} — ${recordFor.employeeName}`
                : ''}
            </DialogTitle>
            <DialogDescription>{t('hr.compliance.recordHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="record-file">{t('hr.compliance.uploadFile')}</Label>
              <Input
                id="record-file"
                ref={recordFileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="record-valid-until">{t('hr.compliance.uploadValidUntil')}</Label>
              <Input
                id="record-valid-until"
                type="date"
                min={todayYmd()}
                value={recordValidUntil}
                onChange={(e) => setRecordValidUntil(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordFor(null)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={recordBusy || !recordValidUntil} onClick={handleRecord}>
              {recordBusy && <Loader2 className="size-4 animate-spin" />}
              {t('hr.compliance.recordSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
